import { useState } from 'react'
import { Shield, User, Mail, Lock, CheckCircle2, AlertTriangle, Building2, CalendarDays, ChevronLeft } from 'lucide-react'
import { auth } from '../api'
import { useAuthStore } from '../stores/auth'

// ZER-321: サーバー側の validatePassword と完全に合わせる（旧クライアントは 8文字、サーバーは 12文字）
function validatePasswordClient(password: string): string | null {
  if (password.length < 12) return 'パスワードは12文字以上にしてください'
  if (password.length > 128) return 'パスワードは128文字以下にしてください'
  if (!/[a-z]/.test(password)) return '小文字の英字を含めてください'
  if (!/[A-Z]/.test(password)) return '大文字の英字を含めてください'
  if (!/[0-9]/.test(password)) return '数字を含めてください'
  if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/.test(password)) return '記号を含めてください（例: !@#$%^&*）'
  const weakPatterns = ['password', '12345', 'qwerty', 'admin', 'letmein']
  const lower = password.toLowerCase()
  if (weakPatterns.some((p) => lower.includes(p))) return '推測されやすい単語を含めないでください'
  return null
}

type Step = 'admin' | 'company'

export default function SetupPage() {
  const checkAuth = useAuthStore((s) => s.checkAuth)
  const [step, setStep] = useState<Step>('admin')

  // Step 1: 管理者
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  // Step 2: 会社情報
  const [companyName, setCompanyName] = useState('')
  const [fiscalYearStartMonth, setFiscalYearStartMonth] = useState(4) // 4月開始（日本標準）

  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [done, setDone] = useState(false)

  const goToCompanyStep = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (!name.trim() || !email.trim()) {
      setError('名前とメールアドレスを入力してください')
      return
    }
    if (password !== confirmPassword) {
      setError('パスワードが一致しません')
      return
    }
    const policyError = validatePasswordClient(password)
    if (policyError) {
      setError(policyError)
      return
    }
    setStep('company')
  }

  const handleFinish = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (!companyName.trim()) {
      setError('会社名（事業者名）を入力してください')
      return
    }
    setLoading(true)
    try {
      await auth.setup({ name, email, password, companyName: companyName.trim(), fiscalYearStartMonth })
      setDone(true)
      setTimeout(() => checkAuth(), 2000)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'セットアップに失敗しました')
    } finally {
      setLoading(false)
    }
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-100">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900">セットアップ完了</h2>
          <p className="text-gray-500 mt-2">ログイン画面に移動します...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-100 mb-4">
            {step === 'admin' ? (
              <Shield className="w-8 h-8 text-indigo-600" />
            ) : (
              <Building2 className="w-8 h-8 text-indigo-600" />
            )}
          </div>
          <h1 className="text-2xl font-bold text-gray-900">初期セットアップ</h1>
          <p className="text-gray-500 mt-1">
            {step === 'admin' ? 'ステップ 1 / 2 ：管理者アカウントを作成' : 'ステップ 2 / 2 ：会社情報を入力'}
          </p>
          {/* ステッパー */}
          <div className="flex items-center justify-center gap-2 mt-3">
            <div className={`h-1.5 w-12 rounded-full ${step === 'admin' ? 'bg-indigo-600' : 'bg-green-500'}`} />
            <div className={`h-1.5 w-12 rounded-full ${step === 'company' ? 'bg-indigo-600' : 'bg-gray-200'}`} />
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 text-red-700 text-sm mb-6">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {step === 'admin' ? (
          <form onSubmit={goToCompanyStep} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">名前</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  placeholder="管理者" required />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">メールアドレス</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  placeholder="admin@example.com" required />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">パスワード（12文字以上・大小英数記号）</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  placeholder="パスワード" required minLength={12} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">パスワード確認</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  placeholder="もう一度入力" required minLength={12} />
              </div>
            </div>

            <button type="submit"
              className="w-full flex items-center justify-center gap-2 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition mt-6">
              次へ：会社情報の入力
            </button>
          </form>
        ) : (
          <form onSubmit={handleFinish} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">会社名（または事業者名）</label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  placeholder="株式会社○○" required maxLength={100} />
              </div>
              <p className="text-xs text-gray-500 mt-1">画面上部のヘッダーや出力資料に表示されます。あとから設定画面で変更できます。</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">会計年度の開始月</label>
              <div className="relative">
                <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <select value={fiscalYearStartMonth} onChange={(e) => setFiscalYearStartMonth(parseInt(e.target.value))}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none appearance-none bg-white">
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <option key={m} value={m}>{m}月開始</option>
                  ))}
                </select>
              </div>
              <p className="text-xs text-gray-500 mt-1">前年度・当年度・翌年度の 3 期分が自動で作成されます。一般的な日本企業は 4 月開始。</p>
            </div>

            <div className="flex items-center gap-2 mt-6">
              <button type="button" onClick={() => setStep('admin')}
                className="flex items-center justify-center gap-1 px-4 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition">
                <ChevronLeft className="w-4 h-4" />
                戻る
              </button>
              <button type="submit" disabled={loading}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white font-medium rounded-lg transition">
                {loading ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" /> : 'セットアップを完了する'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
